// =============================================================================
// pageSize.ts — the ONE source of truth for how many rows a list shows per page
// =============================================================================
// Testing showed people on smaller screens struggled with 20 rows (John, 6 Aug
// 2026). Rather than pick a number that's wrong for somebody either way, the size
// is a per-DEVICE preference: default 12, changeable from a "Rows" selector on any
// list, remembered in localStorage.
//
// WHY per device and not per user account: the setting exists because of SCREEN
// SIZE, which belongs to the machine, not the person. Someone on a small laptop
// and a large desktop gets the right number on each without thinking about it —
// storing it on their profile would follow them and be wrong on one of them.
//
// ONE key for ALL lists: set it once anywhere and every paginated list follows.
// Setting it per screen would mean choosing it fourteen times, which is worse than
// the problem being solved. Previously each screen hard-coded its own (20/25/50);
// those are all gone — John's call, 6 Aug: no deliberate exceptions.
// =============================================================================

const STORAGE_KEY = "zephyr.rowsPerPage";

export const PAGE_SIZE_DEFAULT = 12;
export const PAGE_SIZE_OPTIONS = [12, 20, 50, 100] as const;

// Coerce anything (a stored string, a stale value, junk) to a valid option.
// Pure — this is the bit worth unit-testing.
export function normalisePageSize(value: unknown): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return PAGE_SIZE_DEFAULT;
  return (PAGE_SIZE_OPTIONS as readonly number[]).includes(n) ? n : PAGE_SIZE_DEFAULT;
}

// In-memory copy so every mounted list reads the same value without each one
// hitting localStorage, and so a change on one list updates the others live.
let current: number | null = null;
const listeners = new Set<(size: number) => void>();

export function getPageSize(): number {
  if (current !== null) return current;
  let stored: string | null = null;
  try {
    stored = globalThis.localStorage?.getItem(STORAGE_KEY) ?? null;
  } catch (_e) {
    // Private browsing / storage disabled — fall back to the default.
  }
  current = normalisePageSize(stored);
  return current;
}

export function setPageSize(value: unknown): number {
  const size = normalisePageSize(value);
  current = size;
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, String(size));
  } catch (_e) {
    // Non-fatal: the choice still applies for this session.
  }
  for (const fn of listeners) fn(size);
  return size;
}

export function subscribeToPageSize(fn: (size: number) => void): () => void {
  listeners.add(fn);
  return function () {
    listeners.delete(fn);
  };
}

// Test seam — resets the memoised value so a test can start clean.
export function _resetPageSizeCache(): void {
  current = null;
}
